import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "@/firebase/config";
import { Timestamp } from "firebase/firestore";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

interface BlogPost {
  id: string;
  title: string;
  contentHtml: string;
  description?: string;
  thumbnailUrl: string;
  author: string;
  createdAt: Timestamp;
}

const Blogs = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const blogsRef = collection(db, "BlogsWeb");
        const q = query(blogsRef, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);

        const blogs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as BlogPost[];

        setBlogPosts(blogs);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const formatDate = (timestamp: Timestamp) => {
    return timestamp.toDate().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cloud-white">
        <Navigation />
        <div className="min-h-[80vh] flex items-center justify-center">
          <span className="text-3xl animate-spin">🔆</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cloud-white">
      <Navigation />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-forest-green to-forest-green/80 text-cloud-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-soft-saffron/20 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-copper-brown/20 to-transparent rounded-full transform -translate-x-24 translate-y-24"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center py-12">
            {/* Back to Home Link */}

            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6">
              Our Blog
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-soft-saffron to-copper-brown rounded-full mx-auto mb-6"></div>
            <p className="text-xl text-cloud-white/90 max-w-2xl mx-auto leading-relaxed">
                   Explore insights and wisdom for your wellness journey
            </p>
          </div>
        </div>
      </section>

      {/* Blog List Section */}
      <section
        className="py-20 relative"
        style={{
          backgroundColor: "white",
          backgroundImage: `
          linear-gradient(30deg, rgba(243, 244, 246, 0.4) 12%, transparent 12.5%, transparent 87%, rgba(243, 244, 246, 0.4) 87.5%, rgba(243, 244, 246, 0.4)),
          linear-gradient(150deg, rgba(243, 244, 246, 0.4) 12%, transparent 12.5%, transparent 87%, rgba(243, 244, 246, 0.4) 87.5%, rgba(243, 244, 246, 0.4))
        `,
          backgroundSize: "80px 140px",
          backgroundPosition: "0 0, 40px 70px",
        }}
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {blogPosts.length === 0 ? (
              <div className="text-center py-20">
                <h3 className="text-2xl font-heading text-forest-green mb-4">
                  No blogs found
                </h3>
                <p className="text-copper-brown">
                  Check back soon for new content!
                </p>
              </div>
            ) : (
              <>
                {/* Blog Count */}
                <div className="mb-12 text-center">
                  {/* <p className="text-copper-brown text-lg">
                    Discover wisdom through our {blogPosts.length} inspiring
                    article
                    {blogPosts.length !== 1 ? "s" : ""}
                  </p> */}
                </div>

                {/* Blog Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {blogPosts.map((post) => (
                    <Card
                      key={post.id}
                      className="group hover:scale-105 transition-all duration-300 border-0 shadow-lg hover:shadow-xl bg-cloud-white overflow-hidden h-full flex flex-col"
                    >
                      <Link
                        to={`/blog/${post.id}`}
                        className="flex flex-col h-full"
                        onClick={() => window.scrollTo(0, 0)}
                      >
                        <div className="relative">
                          <img
                            src={post.thumbnailUrl || "/placeholder.svg"}
                            alt={post.title}
                            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute top-4 left-4">
                            <span className="bg-cloud-white text-forest-green px-3 py-1 rounded-full text-sm font-medium shadow-md">
                              By {post.author}
                            </span>
                          </div>
                        </div>

                        <CardContent className="p-6 flex-1 flex flex-col">
                          <h3 className="font-heading text-xl font-semibold text-forest-green mb-3 group-hover:text-copper-brown transition-colors duration-300 line-clamp-2">
                            {post.title}
                          </h3>

                          <p className="text-copper-brown leading-relaxed mb-4 flex-1 line-clamp-3">
                            {post.description ||
                              post.contentHtml
                                .substring(0, 150)
                                .replace(/<[^>]*>/g, "")}
                            ...
                          </p>

                          {/* Blog Meta */}
                          <div className="flex items-center justify-between text-sm text-copper-brown/70 mb-4">
                            <div className="flex items-center gap-1">
                              {/* <span className="text-sm">📅</span> */}
                              <span>{formatDate(post.createdAt)}</span>
                            </div>
                            {/* <div className="flex items-center gap-1">
                              <span className="text-sm">🌿</span>
                              <span>{post.author}</span>
                            </div> */}
                          </div>

                          <div className="flex items-center justify-between mt-auto">
                            <span className="text-soft-saffron font-medium hover:text-copper-brown transition-colors">
                              Read More →
                            </span>
                            <div className="w-0 h-1 bg-gradient-to-r from-soft-saffron to-copper-brown rounded-full group-hover:w-8 transition-all duration-300"></div>
                          </div>
                        </CardContent>
                      </Link>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blogs;
